import {
  DeclarativeMapImplementation,
  ElementIdentifier,
  LayerDefinition,
  MAP_PANE_ORDER,
  MAP_PANE_Z_INDEX_STRIDE,
  MapPane,
  MarkerDefinition,
  PaintArrow,
  PaintArrowDefinition,
  PolylineDefinition,
  RotationSource,
} from '@cue/map/core';
import { inject, NgZone } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Subject } from 'rxjs';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import OlMap from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import { defaults as defaultControls } from 'ol/control/defaults';
import CircleGeom from 'ol/geom/Circle';
import Geometry from 'ol/geom/Geometry';
import DragRotate from 'ol/interaction/DragRotate';
import LineString from 'ol/geom/LineString';
import PinchRotate from 'ol/interaction/PinchRotate';
import Point from 'ol/geom/Point';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import Projection from 'ol/proj/Projection';
import ImageStatic from 'ol/source/ImageStatic';
import VectorSource from 'ol/source/Vector';
import { asArray, Color } from 'ol/color';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';

type MapExtent = [number, number, number, number];
type Padding = [number, number, number, number];

type ImageEntry = {
  layer: ImageLayer<ImageStatic>;
  extent: MapExtent;
};

type LayerEntry = {
  layer: VectorLayer<VectorSource>;
  feature: Feature<Geometry>;
  style: Style;
  radius?: number;
  interactive: boolean;
  fill: boolean;
  fillColor: string;
  fillOpacity: number;
};

type MarkerEntry = {
  overlay: Overlay;
  overlayElement?: HTMLElement;
  element: HTMLElement;
  clickListener: (event: MouseEvent) => void;
  coordinate: [number, number];
  className?: string;
  zIndexOffset: number;
  pane?: MapPane | null;
};

type PolylineEntry = {
  layer: VectorLayer<VectorSource>;
  feature: Feature<LineString>;
  style: Style;
  className?: string;
  animated: boolean;
  dashOffset: number;
};

export class OpenLayersDeclarativeMapImplementation implements DeclarativeMapImplementation, PaintArrow {
  private static readonly ANIMATED_PATH_CLASS = 'animated-path';
  private static readonly ANIMATED_PATH_DASH: number[] = [10, 10];
  private static readonly DEFAULT_PATH_DASH: number[] = [5, 10];
  private static readonly ANIMATED_PATH_SPEED = 50;
  private static readonly ROTATION_STEP = Math.PI / 12;

  #currentLocationClicked = new Subject<void>();
  currentLocationClicked = this.#currentLocationClicked.asObservable();
  #mapReady = new BehaviorSubject(false);
  #layerClicked = new Subject<ElementIdentifier>();
  #layerAppended = new Subject<ElementIdentifier>();
  layerAppended = this.#layerAppended.asObservable();
  #layerRemoved = new Subject<ElementIdentifier>();
  layerRemoved = this.#layerRemoved.asObservable();
  layerClicked = this.#layerClicked.asObservable();

  #markerAppended = new Subject<ElementIdentifier>();
  markerAppended = this.#markerAppended.asObservable();
  #markerRemoved = new Subject<ElementIdentifier>();
  markerRemoved = this.#markerRemoved.asObservable();
  #markerClicked = new Subject<ElementIdentifier>();
  markerClicked = this.#markerClicked.asObservable();

  #polylineRepainted = new Subject<ElementIdentifier>();
  polylineRepainted = this.#polylineRepainted.asObservable();
  #polylineAppended = new Subject<ElementIdentifier>();
  polylineAppended = this.#polylineAppended.asObservable();
  #polylineRemoved = new Subject<ElementIdentifier>();
  polylineRemoved = this.#polylineRemoved.asObservable();
  #imageAppended = new Subject<ElementIdentifier>();
  imageAppended = this.#imageAppended.asObservable();
  #zoomChanged = new Subject<number>();
  zoomChanged = this.#zoomChanged.asObservable();

  #debug = false;
  #map?: OlMap;
  #projection?: Projection;
  #defaults?: { zoom: number; minZoom: number; maxZoom: number };
  #boundResizeObserver?: ResizeObserver;
  #geoJson = new GeoJSON();
  #destroyed = false;

  #zooming = false;
  #moving = false;
  #zoomingEndTimer?: ReturnType<typeof setTimeout>;
  #lastPublishedZoom?: number;
  #vectorRepaintFrame?: number;
  #fpsMonitorFrame?: number;
  #fpsLastTimestamp?: number;
  #fpsEma = 60;
  #lowFpsFrameStreak = 0;
  #highFpsFrameStreak = 0;
  #interactionSurfaceBoostEnabled = true;
  #polylineAnimationFrame?: number;
  #polylineAnimationLastTimestamp?: number;

  #imageOverlays: Map<ElementIdentifier, ImageEntry> = new Map();
  #layers: Map<ElementIdentifier, LayerEntry> = new Map();
  #markers: Map<ElementIdentifier, MarkerEntry> = new Map();
  #polylines: Map<ElementIdentifier, PolylineEntry> = new Map();

  #controlsContainer?: HTMLDivElement;
  #controlsBottomRight?: HTMLDivElement;
  #zoomInButton?: HTMLAnchorElement;
  #zoomOutButton?: HTMLAnchorElement;
  #currentLocationControl?: HTMLDivElement;
  #rotateControlContainer?: HTMLDivElement;
  #gpsAutoRotateButton?: HTMLAnchorElement;
  #gpsAutoRotateAvailable = false;
  #gpsAutoRotateEnabled = false;
  #targetPointerLeaveHandler?: EventListener;
  #rotationEnabled = true;
  #paintedArrowLayer?: VectorLayer<VectorSource>;

  #zone = inject(NgZone);

  enableDebug() {
    this.#debug = true;
  }

  supportsRotation(): boolean {
    return true;
  }

  setRotationEnabled(enabled: boolean): void {
    this.#rotationEnabled = enabled;
    this.applyRotationState();
  }

  setGpsAutoRotateAvailable(available: boolean): void {
    this.#gpsAutoRotateAvailable = available;
    this.applyGpsAutoRotateControlState();
  }

  setRotation(rotation: number, animate = false, source: RotationSource = 'manual'): void {
    if (!Number.isFinite(rotation)) {
      return;
    }
    const normalizedRotation = this.normalizeRotation(rotation);
    if (source === 'gps' && (!this.#gpsAutoRotateAvailable || !this.#gpsAutoRotateEnabled)) {
      return;
    }
    if (!this.#rotationEnabled && normalizedRotation !== 0) {
      return;
    }

    const view = this.#map?.getView();
    if (!view) {
      return;
    }

    if (animate) {
      const currentRotation = this.normalizeRotation(view.getRotation() ?? 0);
      const rotationDelta = this.normalizeRotation(normalizedRotation - currentRotation);
      const animationTarget = currentRotation + rotationDelta;
      view.cancelAnimations();
      view.animate({
        rotation: animationTarget,
        duration: 180,
      });
    } else {
      view.setRotation(normalizedRotation);
    }
    this.requestVectorSurfaceRepaint();
    this.refreshMarkerZIndices();
  }

  private toggleGpsAutoRotate() {
    this.#gpsAutoRotateEnabled = !this.#gpsAutoRotateEnabled;
    this.applyGpsAutoRotateControlState();
  }

  onMapReady(action: (map: OlMap) => void) {
    firstValueFrom(this.#mapReady.pipe(filter((ready) => ready))).then(() => {
      if (this.#map) {
        action(this.#map);
      }
    });
  }

  focusLayer(identifier: ElementIdentifier) {
    const found = this.#layers.get(identifier);
    if (!found) {
      return;
    }
    if (this.#debug) {
      console.log('Focusing layer', identifier, found.feature.getGeometry()?.getExtent());
    }
    this.fitGeometry(found.feature.getGeometry());
  }

  private removeBackToStart() {
    this.#currentLocationControl?.remove();
    this.#currentLocationControl = undefined;
  }

  private paintBackToStart() {
    this.onMapReady(() => {
      if (this.#currentLocationControl || !this.#controlsBottomRight) {
        return;
      }

      const container = document.createElement('div');
      container.className = 'leaflet-bar leaflet-control';

      const button = this.createControlButton('back-to-start', 'Back To Start', this.locationSvg(), () => {
        this.#currentLocationClicked.next();
      });

      container.appendChild(button);
      this.#controlsBottomRight.appendChild(container);
      this.#currentLocationControl = container;
    });
  }

  showCurrentLocationButton(show: boolean) {
    if (show) {
      this.paintBackToStart();
    } else {
      this.removeBackToStart();
    }
  }

  invalidateMapSize(): void {
    this.onMapReady(() => {
      this.#map?.updateSize();
      this.requestVectorSurfaceRepaint();
      const existingImage = this.#imageOverlays.entries().next().value as [ElementIdentifier, ImageEntry] | undefined;
      if (existingImage) {
        this.fitExtent(existingImage[1].extent, [0, 0, 0, 0], false);
      }
    });
  }

  focusMarker(identifier: ElementIdentifier, align?: 'top-center') {
    const found = this.#markers.get(identifier);
    if (!found || !this.#map) {
      return;
    }

    if (this.#debug) {
      console.log('Focusing marker', identifier, found.coordinate);
    }

    if (align === 'top-center') {
      const pixel = this.#map.getPixelFromCoordinate(found.coordinate);
      const size = this.#map.getSize();
      if (pixel && size) {
        const offsetPixel: [number, number] = [pixel[0], pixel[1] + size[1] / 2 - 50];
        const offsetCoordinate = this.#map.getCoordinateFromPixel(offsetPixel);
        if (offsetCoordinate) {
          this.#map.getView().animate({ center: offsetCoordinate, duration: 250 });
          return;
        }
      }
    }

    this.#map.getView().animate({ center: found.coordinate, duration: 250 });
  }

  private customMapControls(nativeElement: HTMLElement): void {
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'leaflet-control-container';
    const bottomRight = document.createElement('div');
    bottomRight.className = 'leaflet-bottom leaflet-right';
    controlsContainer.appendChild(bottomRight);
    nativeElement.appendChild(controlsContainer);

    this.#controlsContainer = controlsContainer;
    this.#controlsBottomRight = bottomRight;

    const zoomContainer = document.createElement('div');
    zoomContainer.className = 'leaflet-bar leaflet-control leaflet-control-zoom';
    this.#zoomInButton = this.createControlButton('leaflet-control-zoom-in', 'Zoom in', this.plusSvg(), () => {
      const view = this.#map?.getView();
      if (!view) {
        return;
      }
      const zoom = view.getZoom() ?? this.#defaults?.zoom ?? 0;
      view.setZoom(zoom + 0.25);
    });
    this.#zoomOutButton = this.createControlButton('leaflet-control-zoom-out', 'Zoom out', this.minusSvg(), () => {
      const view = this.#map?.getView();
      if (!view) {
        return;
      }
      const zoom = view.getZoom() ?? this.#defaults?.zoom ?? 0;
      view.setZoom(zoom - 0.25);
    });
    zoomContainer.append(this.#zoomInButton, this.#zoomOutButton);

    const resetContainer = document.createElement('div');
    resetContainer.className = 'leaflet-bar leaflet-control';
    const resetButton = this.createControlButton('reset-view', 'Reset View', this.resetSvg(), () => {
      this.rotateTo(0);
      const firstImage = this.#imageOverlays.entries().next().value as [ElementIdentifier, ImageEntry] | undefined;
      if (!firstImage) {
        return;
      }
      this.fitExtent(firstImage[1].extent, [0, 0, 0, 0], false);
      // if ((this.#defaults?.zoom ?? 0) >= 1) {
      //   this.setZoom(firstImage[0], this.#defaults!.zoom);
      // }
    });
    resetContainer.appendChild(resetButton);

    if (this.supportsRotation()) {
      const rotateContainer = document.createElement('div');
      rotateContainer.className = 'leaflet-bar leaflet-control leaflet-control-zoom leaflet-control-rotate';
      const rotateLeftButton = this.createControlButton('rotate-left', 'Rotate left', this.rotateLeftSvg(), () => {
        this.rotateBy(-OpenLayersDeclarativeMapImplementation.ROTATION_STEP);
      });
      const rotateRightButton = this.createControlButton('rotate-right', 'Rotate right', this.rotateRightSvg(), () => {
        this.rotateBy(OpenLayersDeclarativeMapImplementation.ROTATION_STEP);
      });
      this.#gpsAutoRotateButton = this.createControlButton(
        'gps-auto-rotate',
        'Toggle GPS auto-rotate',
        this.gpsAutoRotateSvg(),
        () => this.toggleGpsAutoRotate()
      );
      rotateContainer.append(rotateRightButton, rotateLeftButton, this.#gpsAutoRotateButton);
      this.#rotateControlContainer = rotateContainer;
      bottomRight.append(resetContainer, rotateContainer, zoomContainer);
    } else {
      bottomRight.append(resetContainer, zoomContainer);
    }

    this.applyGpsAutoRotateControlState();
    this.applyRotationState();
    this.updateZoomButtonState();
  }

  private rotateBy(delta: number) {
    if (!this.#rotationEnabled) {
      return;
    }
    const view = this.#map?.getView();
    if (!view) {
      return;
    }
    const currentRotation = view.getRotation() ?? 0;
    this.setRotation(currentRotation + delta, true);
  }

  private rotateTo(rotation: number) {
    this.setRotation(rotation, true);
  }

  private createControlButton(className: string, title: string, html: string, onClick: () => void): HTMLAnchorElement {
    const button = document.createElement('a');
    button.className = `leaflet-control-button ${className}`;
    button.title = title;
    button.href = '#';
    button.innerHTML = html;
    this.preventMapEventPropagation(button);
    button.onclick = (event) => {
      event.preventDefault();
      onClick();
      this.updateZoomButtonState();
    };
    return button;
  }

  private preventMapEventPropagation(element: HTMLElement) {
    const stop = (event: Event) => {
      event.stopPropagation();
    };
    element.addEventListener('click', stop);
    element.addEventListener('dblclick', stop);
    element.addEventListener('mousedown', stop);
    element.addEventListener('pointerdown', stop);
    element.addEventListener('touchstart', stop, { passive: true });
  }

  private setMapCursor(cursor: '' | 'pointer') {
    const targetElement = this.#map?.getTargetElement() as HTMLElement | undefined;
    if (!targetElement) {
      return;
    }
    targetElement.style.setProperty('cursor', cursor);
  }

  private applyRotationState() {
    const rotationEnabled = this.supportsRotation() && this.#rotationEnabled;

    if (this.#rotateControlContainer) {
      this.#rotateControlContainer.style.display = rotationEnabled ? '' : 'none';
    }

    if (!this.#map) {
      return;
    }

    this.#map.getInteractions().forEach((interaction) => {
      if (interaction instanceof DragRotate || interaction instanceof PinchRotate) {
        interaction.setActive(rotationEnabled);
      }
    });

    if (!rotationEnabled) {
      this.#map.getView().setRotation(0);
      this.requestVectorSurfaceRepaint();
      this.refreshMarkerZIndices();
    }

    this.applyGpsAutoRotateControlState();
  }

  private applyGpsAutoRotateControlState() {
    if (!this.#gpsAutoRotateButton) {
      return;
    }

    const shouldShow = this.supportsRotation() && this.#rotationEnabled && this.#gpsAutoRotateAvailable;
    this.#gpsAutoRotateButton.style.display = shouldShow ? '' : 'none';
    this.#gpsAutoRotateButton.classList.toggle('active', this.#gpsAutoRotateEnabled);
    this.#gpsAutoRotateButton.setAttribute('aria-pressed', this.#gpsAutoRotateEnabled ? 'true' : 'false');
    this.#gpsAutoRotateButton.title = this.#gpsAutoRotateEnabled ? 'Disable GPS auto-rotate' : 'Enable GPS auto-rotate';
  }

  private updateZoomButtonState() {
    const map = this.#map;
    if (!map || !this.#zoomInButton || !this.#zoomOutButton) {
      return;
    }
    const view = map.getView();
    const rawZoom = view.getZoom();
    const zoom = typeof rawZoom === 'number' && Number.isFinite(rawZoom) ? rawZoom : (this.#defaults?.zoom ?? 0);
    const maxZoom = view.getMaxZoom();
    const minZoom = view.getMinZoom();
    this.#zoomInButton.classList.toggle('leaflet-disabled', zoom >= maxZoom);
    this.#zoomOutButton.classList.toggle('leaflet-disabled', zoom <= minZoom);
  }

  removeLayer(identifier: ElementIdentifier): void {
    const found = this.#layers.get(identifier);
    if (!found || !this.#map) {
      return;
    }
    this.#map.removeLayer(found.layer);
    this.#layers.delete(identifier);
    this.#layerRemoved.next(identifier);
  }

  editMarker(identifier: ElementIdentifier, markerDefinitionPatch: Partial<MarkerDefinition>) {
    const foundMarker = this.#markers.get(identifier);
    if (!foundMarker || !this.#map) {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(markerDefinitionPatch, 'geoJson')) {
      const coordinate = this.extractPointCoordinate(markerDefinitionPatch.geoJson);
      if (coordinate) {
        this.reprojectWhileNotAnimating(() => {
          foundMarker.coordinate = coordinate;
          foundMarker.overlay.setPosition(coordinate);
          this.applyMarkerZIndex(foundMarker);
        });
      } else if (this.#debug) {
        console.warn('Skipping marker update - invalid point geometry', identifier, markerDefinitionPatch.geoJson);
      }
    }

    if (Object.prototype.hasOwnProperty.call(markerDefinitionPatch, 'element')) {
      foundMarker.element.removeEventListener('click', foundMarker.clickListener);
      foundMarker.element = markerDefinitionPatch.element ?? document.createElement('div');
      foundMarker.overlay.setElement(foundMarker.element);
      foundMarker.overlayElement = this.getOverlayElement(foundMarker.overlay);
      foundMarker.element.addEventListener('click', foundMarker.clickListener);
      this.applyMarkerClassName(foundMarker);
      this.applyMarkerZIndex(foundMarker);
    }

    if (Object.prototype.hasOwnProperty.call(markerDefinitionPatch, 'className')) {
      const previousClassName = foundMarker.className;
      foundMarker.className = markerDefinitionPatch.className ?? undefined;
      this.applyMarkerClassName(foundMarker, previousClassName);
    }

    if (Object.prototype.hasOwnProperty.call(markerDefinitionPatch, 'zIndexOffset')) {
      foundMarker.zIndexOffset = markerDefinitionPatch.zIndexOffset ?? 0;
      this.applyMarkerZIndex(foundMarker);
    }

    if (Object.prototype.hasOwnProperty.call(markerDefinitionPatch, 'pane')) {
      foundMarker.pane = markerDefinitionPatch.pane;
      this.applyMarkerZIndex(foundMarker);
    }

    this.refreshMarkerZIndices();
  }

  private applyMarkerZIndex(marker: MarkerEntry) {
    const paneOffset = this.getPaneZIndex(marker.pane, MapPane.Poi);
    const markerPixel = this.#map?.getPixelFromCoordinate(marker.coordinate);
    const yOffset = markerPixel ? Math.round(markerPixel[1]) : 0;
    const zIndex = paneOffset + yOffset + marker.zIndexOffset;
    const overlayElement = marker.overlayElement ?? this.getOverlayElement(marker.overlay);
    if (overlayElement) {
      marker.overlayElement = overlayElement;
      overlayElement.style.zIndex = String(zIndex);
    }
    marker.element.style.zIndex = String(zIndex);
  }

  private getPaneZIndex(pane: MapPane | null | undefined, fallback: MapPane): number {
    return MAP_PANE_ORDER[pane ?? fallback] * MAP_PANE_Z_INDEX_STRIDE;
  }

  private getLayerClassName(pane: MapPane | null | undefined, fallback: MapPane): string {
    return `cue-map-pane-${pane ?? fallback}`;
  }

  private configureLayerPane(
    layer: ImageLayer<ImageStatic> | VectorLayer<VectorSource>,
    pane: MapPane | null | undefined,
    fallback: MapPane
  ): void {
    const zIndex = this.getPaneZIndex(pane, fallback);
    layer.setZIndex(zIndex);
    layer.once('postrender', () => {
      const renderer = layer.getRenderer() as unknown as { container?: HTMLElement } | null;
      if (!renderer?.container) {
        return;
      }
      renderer.container.style.zIndex = String(zIndex);
      renderer.container.style.pointerEvents = 'none';
    });
  }

  private configurePaneStacking(): void {
    if (!this.#map) {
      return;
    }
    const viewport = this.#map.getViewport();
    viewport.style.isolation = 'isolate';
    const layerContainer = viewport.querySelector<HTMLElement>(':scope > .ol-layers');
    layerContainer?.style.removeProperty('z-index');
    this.#map.getOverlayContainer().style.removeProperty('z-index');
    this.#map.getOverlayContainerStopEvent().style.removeProperty('z-index');
  }

  private getOverlayElement(overlay: Overlay): HTMLElement | undefined {
    return (overlay as any).element as HTMLElement | undefined;
  }

  private applyMarkerClassName(marker: MarkerEntry, previousClassName?: string) {
    const overlayElement = marker.overlayElement ?? this.getOverlayElement(marker.overlay);
    if (!overlayElement) {
      return;
    }
    marker.overlayElement = overlayElement;
    overlayElement.style.setProperty('cursor', 'pointer');
    overlayElement.style.setProperty('touch-action', 'manipulation');
    overlayElement.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    if (previousClassName) {
      overlayElement.classList.remove(previousClassName);
    }
    if (marker.className) {
      overlayElement.classList.add(marker.className);
    }
  }

  private refreshMarkerZIndices() {
    this.#markers.forEach((entry) => this.applyMarkerZIndex(entry));
  }

  private requestVectorSurfaceRepaint() {
    if (!this.#interactionSurfaceBoostEnabled) {
      return;
    }
    this.#layers.forEach((entry) => entry.layer.changed());
    this.#polylines.forEach((entry) => entry.layer.changed());
    if (!this.#map || this.#vectorRepaintFrame !== undefined) {
      return;
    }
    this.#vectorRepaintFrame = requestAnimationFrame(() => {
      this.#vectorRepaintFrame = undefined;
      this.#map?.render();
    });
  }

  private applyInteractionSurfaceBoostToLayer(layer: VectorLayer<VectorSource>) {
    const layerAny = layer as any;
    layerAny.updateWhileAnimating_ = this.#interactionSurfaceBoostEnabled;
    layerAny.updateWhileInteracting_ = this.#interactionSurfaceBoostEnabled;
    layerAny.renderBuffer_ = this.#interactionSurfaceBoostEnabled ? 512 : 100;
    layer.changed();
  }

  private applyInteractionSurfaceBoostToExistingLayers() {
    this.#layers.forEach((entry) => this.applyInteractionSurfaceBoostToLayer(entry.layer));
    this.#polylines.forEach((entry) => this.applyInteractionSurfaceBoostToLayer(entry.layer));
  }

  private setInteractionSurfaceBoostEnabled(enabled: boolean, currentFps: number) {
    if (this.#interactionSurfaceBoostEnabled === enabled) {
      return;
    }
    this.#interactionSurfaceBoostEnabled = enabled;
    this.#lowFpsFrameStreak = 0;
    this.#highFpsFrameStreak = 0;
    this.applyInteractionSurfaceBoostToExistingLayers();
    if (enabled) {
      console.info(`[OpenLayers] Surface interaction repaint ENABLED, stable FPS ${currentFps.toFixed(1)}`);
      this.requestVectorSurfaceRepaint();
      return;
    }
    console.warn(`[OpenLayers] Surface interaction repaint DISABLED, low FPS ${currentFps.toFixed(1)}`);
  }

  private stopFpsMonitor() {
    if (this.#fpsMonitorFrame !== undefined) {
      cancelAnimationFrame(this.#fpsMonitorFrame);
      this.#fpsMonitorFrame = undefined;
    }
    this.#fpsLastTimestamp = undefined;
    this.#fpsEma = 60;
    this.#lowFpsFrameStreak = 0;
    this.#highFpsFrameStreak = 0;
  }

  private isAnimatedPathClass(className?: string | null): boolean {
    if (!className) {
      return false;
    }
    return className
      .split(/\s+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .includes(OpenLayersDeclarativeMapImplementation.ANIMATED_PATH_CLASS);
  }

  private getOrCreatePolylineStroke(entry: PolylineEntry): Stroke {
    const existingStroke = entry.style.getStroke();
    if (existingStroke) {
      return existingStroke;
    }
    const createdStroke = new Stroke({
      color: '#000000',
      width: 5,
      lineDash: OpenLayersDeclarativeMapImplementation.DEFAULT_PATH_DASH,
      lineDashOffset: 0,
    });
    entry.style.setStroke(createdStroke);
    return createdStroke;
  }

  private applyPolylineVisualState(entry: PolylineEntry) {
    const stroke = this.getOrCreatePolylineStroke(entry);
    if (entry.animated) {
      stroke.setLineDash(OpenLayersDeclarativeMapImplementation.ANIMATED_PATH_DASH);
      stroke.setLineDashOffset(entry.dashOffset);
    } else {
      stroke.setLineDash(OpenLayersDeclarativeMapImplementation.DEFAULT_PATH_DASH);
      stroke.setLineDashOffset(0);
    }
    entry.layer.changed();
  }

  private applyPolylineClassName(entry: PolylineEntry, className?: string | null) {
    entry.className = className ?? undefined;
    const shouldAnimate = this.isAnimatedPathClass(entry.className);
    if (entry.animated !== shouldAnimate) {
      entry.animated = shouldAnimate;
      if (!entry.animated) {
        entry.dashOffset = 0;
      }
    }
    this.applyPolylineVisualState(entry);
    if (entry.animated) {
      this.startPolylineAnimationLoop();
    }
  }

  private startPolylineAnimationLoop() {
    if (this.#polylineAnimationFrame !== undefined || !this.#map) {
      return;
    }
    const hasAnimatedPolylines = Array.from(this.#polylines.values()).some((entry) => entry.animated);
    if (!hasAnimatedPolylines) {
      return;
    }

    const animate = (timestamp: number) => {
      if (this.#destroyed || !this.#map) {
        this.#polylineAnimationFrame = undefined;
        this.#polylineAnimationLastTimestamp = undefined;
        return;
      }
      if (this.#polylineAnimationLastTimestamp === undefined) {
        this.#polylineAnimationLastTimestamp = timestamp;
      }
      const deltaSeconds = Math.max((timestamp - this.#polylineAnimationLastTimestamp) / 1000, 0);
      this.#polylineAnimationLastTimestamp = timestamp;

      let hasAnimated = false;
      this.#polylines.forEach((entry) => {
        if (!entry.animated) {
          return;
        }
        hasAnimated = true;
        entry.dashOffset -= OpenLayersDeclarativeMapImplementation.ANIMATED_PATH_SPEED * deltaSeconds;
        const stroke = entry.style.getStroke();
        if (stroke) {
          stroke.setLineDashOffset(entry.dashOffset);
        }
        entry.layer.changed();
      });

      if (!hasAnimated) {
        this.#polylineAnimationFrame = undefined;
        this.#polylineAnimationLastTimestamp = undefined;
        return;
      }

      this.#map.render();
      this.#polylineAnimationFrame = requestAnimationFrame(animate);
    };

    this.#polylineAnimationFrame = requestAnimationFrame(animate);
  }

  private stopPolylineAnimationLoop() {
    if (this.#polylineAnimationFrame !== undefined) {
      cancelAnimationFrame(this.#polylineAnimationFrame);
      this.#polylineAnimationFrame = undefined;
    }
    this.#polylineAnimationLastTimestamp = undefined;
  }

  private startFpsMonitor() {
    this.stopFpsMonitor();
    const monitor = (timestamp: number) => {
      if (this.#destroyed || !this.#map) {
        this.#fpsMonitorFrame = undefined;
        return;
      }
      if (this.#fpsLastTimestamp !== undefined) {
        const delta = timestamp - this.#fpsLastTimestamp;
        if (delta > 0 && Number.isFinite(delta)) {
          const instantaneousFps = 1000 / delta;
          const smoothingFactor = 0.12;
          this.#fpsEma = this.#fpsEma * (1 - smoothingFactor) + instantaneousFps * smoothingFactor;
          if (this.#interactionSurfaceBoostEnabled) {
            if (this.#fpsEma < 20) {
              this.#lowFpsFrameStreak += 1;
            } else {
              this.#lowFpsFrameStreak = 0;
            }
            if (this.#lowFpsFrameStreak >= 12) {
              this.setInteractionSurfaceBoostEnabled(false, this.#fpsEma);
            }
          } else {
            if (this.#fpsEma > 30) {
              this.#highFpsFrameStreak += 1;
            } else {
              this.#highFpsFrameStreak = 0;
            }
            if (this.#highFpsFrameStreak >= 60) {
              this.setInteractionSurfaceBoostEnabled(true, this.#fpsEma);
            }
          }
        }
      }
      this.#fpsLastTimestamp = timestamp;
      this.#fpsMonitorFrame = requestAnimationFrame(monitor);
    };
    this.#fpsMonitorFrame = requestAnimationFrame(monitor);
  }

  removeMarker(identifier: ElementIdentifier): void {
    const found = this.#markers.get(identifier);
    if (!found || !this.#map) {
      return;
    }
    found.element.removeEventListener('click', found.clickListener);
    this.#map.removeOverlay(found.overlay);
    this.#markers.delete(identifier);
    this.#markerRemoved.next(identifier);
  }

  editPolyline(identifier: ElementIdentifier, polylineDefinitionPatch: Partial<PolylineDefinition>) {
    const found = this.#polylines.get(identifier);
    if (!found) {
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(polylineDefinitionPatch, 'pointlist') &&
      polylineDefinitionPatch.pointlist
    ) {
      this.reprojectWhileNotAnimating(() => {
        const line = found.feature.getGeometry();
        line.setCoordinates(this.toLineCoordinates(polylineDefinitionPatch.pointlist));
        setTimeout(() => {
          this.#polylineRepainted.next(identifier);
        }, 200);
      });
    }

    if (
      Object.prototype.hasOwnProperty.call(polylineDefinitionPatch, 'color') &&
      polylineDefinitionPatch.color !== undefined
    ) {
      const stroke = this.getOrCreatePolylineStroke(found);
      stroke.setColor(polylineDefinitionPatch.color);
      this.applyPolylineVisualState(found);
    }

    if (Object.prototype.hasOwnProperty.call(polylineDefinitionPatch, 'className')) {
      this.applyPolylineClassName(found, polylineDefinitionPatch.className);
    }

    if (Object.prototype.hasOwnProperty.call(polylineDefinitionPatch, 'pane')) {
      found.layer.setZIndex(this.getPaneZIndex(polylineDefinitionPatch.pane, MapPane.Path));
    }
  }

  removePolyline(identifier: ElementIdentifier) {
    const found = this.#polylines.get(identifier);
    if (!found || !this.#map) {
      return;
    }
    this.#map.removeLayer(found.layer);
    this.#polylines.delete(identifier);
    if (this.#polylines.size === 0) {
      this.stopPolylineAnimationLoop();
    }
    this.#polylineRemoved.next(identifier);
  }

  focusPolyline(identifier: ElementIdentifier) {
    const found = this.#polylines.get(identifier);
    if (!found) {
      return;
    }

    if (this.#debug) {
      console.log('Focusing polyline', identifier, found.feature.getGeometry().getExtent());
    }

    this.executeWhenStationary(() => {
      this.fitGeometry(found.feature.getGeometry(), [30, 30, 30, 30]);
    });
  }

  addPolyline(identifier: ElementIdentifier, polylineDefinition: PolylineDefinition) {
    this.onMapReady(() => {
      this.reprojectWhileNotAnimating(() => {
        const feature = new Feature(new LineString(this.toLineCoordinates(polylineDefinition.pointlist)));
        feature.set('cueElementIdentifier', identifier);
        feature.set('cueElementType', 'polyline');

        const style = new Style({
          stroke: new Stroke({
            color: polylineDefinition.color,
            lineDash: OpenLayersDeclarativeMapImplementation.DEFAULT_PATH_DASH,
            lineDashOffset: 0,
            width: 5,
          }),
        });

        const source = new VectorSource({
          features: [feature],
        });

        const layer = new VectorLayer({
          source,
          style,
          className: this.getLayerClassName(polylineDefinition.pane, MapPane.Path),
          zIndex: this.getPaneZIndex(polylineDefinition.pane, MapPane.Path),
          updateWhileAnimating: this.#interactionSurfaceBoostEnabled,
          updateWhileInteracting: this.#interactionSurfaceBoostEnabled,
          renderBuffer: this.#interactionSurfaceBoostEnabled ? 512 : 100,
        });
        this.configureLayerPane(layer, polylineDefinition.pane, MapPane.Path);
        this.applyInteractionSurfaceBoostToLayer(layer);

        this.#map?.addLayer(layer);
        const entry: PolylineEntry = {
          feature,
          layer,
          style,
          className: undefined,
          animated: false,
          dashOffset: 0,
        };
        this.#polylines.set(identifier, entry);
        this.applyPolylineClassName(entry, polylineDefinition.className);
        this.#polylineAppended.next(identifier);
      });
    });
  }

  addMarker(identifier: ElementIdentifier, markerDefinition: MarkerDefinition, fitMap?: boolean): void {
    this.onMapReady(() => {
      this.reprojectWhileNotAnimating(() => {
        const coordinate = this.extractPointCoordinate(markerDefinition.geoJson);
        if (!coordinate || !this.#map) {
          if (this.#debug) {
            console.warn('Skipping marker - invalid point geometry', identifier, markerDefinition.geoJson);
          }
          return;
        }

        const element = markerDefinition.element ?? document.createElement('div');
        element.style.setProperty('cursor', 'pointer');
        element.style.setProperty('touch-action', 'manipulation');
        element.style.setProperty('-webkit-tap-highlight-color', 'transparent');

        const clickListener = () => {
          this.#markerClicked.next(identifier);
        };
        element.addEventListener('click', clickListener);

        const overlay = new Overlay({
          element,
          className: 'leaflet-marker-icon ol-overlay-container ol-selectable',
          position: coordinate,
          positioning: 'center-center',
          stopEvent: true,
          insertFirst: false,
        });

        const entry: MarkerEntry = {
          overlay,
          overlayElement: this.getOverlayElement(overlay),
          element,
          clickListener,
          coordinate,
          className: markerDefinition.className ?? undefined,
          zIndexOffset: markerDefinition.zIndexOffset ?? 0,
          pane: markerDefinition.pane,
        };
        entry.overlayElement?.style.setProperty('cursor', 'pointer');
        entry.overlayElement?.style.setProperty('touch-action', 'manipulation');
        entry.overlayElement?.style.setProperty('-webkit-tap-highlight-color', 'transparent');
        this.applyMarkerClassName(entry);
        this.applyMarkerZIndex(entry);

        this.#map.addOverlay(overlay);
        this.#markers.set(identifier, entry);
        this.refreshMarkerZIndices();
        this.#markerAppended.next(identifier);
        this.#map.updateSize();

        if (fitMap) {
          const firstImage = this.#imageOverlays.entries().next().value as [ElementIdentifier, ImageEntry] | undefined;
          if (firstImage) {
            this.fitExtent(firstImage[1].extent, [0, 0, 0, 0], false);
          }
          this.#map.getView().setCenter(coordinate);
        }
      });
    });
  }

  addLayer(identifier: ElementIdentifier, polygonDefinition: LayerDefinition, radius?: number, fitMap?: boolean) {
    this.onMapReady(() => {
      const feature = this.createLayerFeature(polygonDefinition.geoJson, radius);
      if (!feature || !this.#map) {
        return;
      }

      feature.set('cueElementIdentifier', identifier);
      feature.set('cueElementType', 'layer');
      feature.set('cueInteractive', polygonDefinition.interactive ?? false);

      const style = this.createLayerStyle(polygonDefinition);
      const source = new VectorSource({
        features: [feature],
      });
      const layer = new VectorLayer({
        source,
        style,
        className: this.getLayerClassName(polygonDefinition.pane, MapPane.Resource),
        zIndex: this.getPaneZIndex(polygonDefinition.pane, MapPane.Resource),
        updateWhileAnimating: this.#interactionSurfaceBoostEnabled,
        updateWhileInteracting: this.#interactionSurfaceBoostEnabled,
        renderBuffer: this.#interactionSurfaceBoostEnabled ? 512 : 100,
      });
      this.configureLayerPane(layer, polygonDefinition.pane, MapPane.Resource);
      this.applyInteractionSurfaceBoostToLayer(layer);

      this.#map.addLayer(layer);
      this.#layers.set(identifier, {
        layer,
        feature,
        style,
        radius,
        interactive: polygonDefinition.interactive ?? false,
        fill: polygonDefinition.fill,
        fillColor: polygonDefinition.fillColor,
        fillOpacity: polygonDefinition.fillOpacity,
      });
      this.#layerAppended.next(identifier);
      if (fitMap) {
        this.fitGeometry(feature.getGeometry(), [0, 0, 0, 0], false);
      }
    });
  }

  scheduleMapAvailable(action: () => void) {
    if (this.#map && this.#mapReady.getValue()) {
      action();
      return;
    }
    this.onMapReady(() => {
      if (this.#destroyed) {
        return;
      }
      action();
    });
  }

  private reprojectWhileNotAnimating(action: () => void) {
    if (!this.#moving || !this.#map) {
      action();
      return;
    }
    this.#map.once('moveend', () => {
      action();
    });
  }

  editLayer(identifier: ElementIdentifier, polygonDefinitionPatch: Partial<LayerDefinition>, radius?: number): void {
    const foundPolygon = this.#layers.get(identifier);
    if (!foundPolygon) {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'geoJson') && polygonDefinitionPatch.geoJson) {
      this.reprojectWhileNotAnimating(() => {
        if (radius && foundPolygon.feature.getGeometry() instanceof CircleGeom) {
          const center = this.extractPointCoordinate(polygonDefinitionPatch.geoJson);
          if (center) {
            (foundPolygon.feature.getGeometry() as CircleGeom).setCenter(center);
          }
        } else {
          const feature = this.createLayerFeature(polygonDefinitionPatch.geoJson);
          if (feature?.getGeometry()) {
            foundPolygon.feature.setGeometry(feature.getGeometry());
          }
        }
      });
    }

    const style = foundPolygon.style;
    const stroke = style.getStroke() ?? new Stroke({ color: '#000000', width: 1 });
    const fill = style.getFill() ?? new Fill({ color: [0, 0, 0, 0] });

    if (
      Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'color') &&
      polygonDefinitionPatch.color !== undefined
    ) {
      stroke.setColor(polygonDefinitionPatch.color);
    }
    if (
      Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'weight') &&
      polygonDefinitionPatch.weight !== undefined
    ) {
      stroke.setWidth(polygonDefinitionPatch.weight);
    }
    if (
      Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'fillColor') &&
      polygonDefinitionPatch.fillColor !== undefined
    ) {
      foundPolygon.fillColor = polygonDefinitionPatch.fillColor;
    }
    if (
      Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'fillOpacity') &&
      polygonDefinitionPatch.fillOpacity !== undefined
    ) {
      foundPolygon.fillOpacity = this.clampOpacity(polygonDefinitionPatch.fillOpacity);
    }
    if (
      Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'fill') &&
      polygonDefinitionPatch.fill !== undefined
    ) {
      foundPolygon.fill = polygonDefinitionPatch.fill;
    }
    if (
      Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'interactive') &&
      polygonDefinitionPatch.interactive !== undefined
    ) {
      foundPolygon.interactive = polygonDefinitionPatch.interactive;
      foundPolygon.feature.set('cueInteractive', polygonDefinitionPatch.interactive);
    }

    fill.setColor(
      foundPolygon.fill ? this.colorWithOpacity(foundPolygon.fillColor, foundPolygon.fillOpacity) : [0, 0, 0, 0]
    );

    style.setStroke(stroke);
    style.setFill(fill);
    foundPolygon.layer.changed();

    if (Object.prototype.hasOwnProperty.call(polygonDefinitionPatch, 'pane')) {
      foundPolygon.layer.setZIndex(this.getPaneZIndex(polygonDefinitionPatch.pane, MapPane.Resource));
    }
  }

  destroy() {
    this.#destroyed = true;
    this.clearArrow();

    if (this.#zoomingEndTimer) {
      clearTimeout(this.#zoomingEndTimer);
      this.#zoomingEndTimer = undefined;
    }
    this.stopPolylineAnimationLoop();
    this.stopFpsMonitor();
    if (this.#vectorRepaintFrame !== undefined) {
      cancelAnimationFrame(this.#vectorRepaintFrame);
      this.#vectorRepaintFrame = undefined;
    }

    this.#markers.forEach((entry) => {
      entry.element.removeEventListener('click', entry.clickListener);
    });

    this.#boundResizeObserver?.disconnect();
    this.#boundResizeObserver = undefined;

    this.#controlsContainer?.remove();
    this.#controlsContainer = undefined;
    this.#controlsBottomRight = undefined;
    this.#currentLocationControl = undefined;
    this.#zoomInButton = undefined;
    this.#zoomOutButton = undefined;
    this.#rotateControlContainer = undefined;
    this.#gpsAutoRotateButton = undefined;
    this.#gpsAutoRotateAvailable = false;
    this.#gpsAutoRotateEnabled = false;

    if (this.#map && this.#targetPointerLeaveHandler) {
      this.#map.getTargetElement()?.removeEventListener('pointerleave', this.#targetPointerLeaveHandler);
      this.#targetPointerLeaveHandler = undefined;
    }

    if (this.#map) {
      this.#map.getOverlays().clear();
      this.#map.getLayers().clear();
      this.#map.setTarget(undefined);
      this.#map = undefined;
    }

    this.#imageOverlays.clear();
    this.#layers.clear();
    this.#markers.clear();
    this.#polylines.clear();
    this.#lastPublishedZoom = undefined;
    this.#mapReady.next(false);
  }

  paintArrow(definition: PaintArrowDefinition): void {
    if (!definition.pointlist.length) {
      this.clearArrow();
      return;
    }

    this.onMapReady(() => {
      this.reprojectWhileNotAnimating(() => {
        if (!this.#map) {
          return;
        }

        this.clearArrow();

        const routeFeature = new Feature(new LineString(this.toLineCoordinates(definition.pointlist)));
        const arrowFeature = new Feature(new Point([definition.end.lng, definition.end.lat]));

        const routeStyle = new Style({
          stroke: new Stroke({
            color: definition.color,
            width: 5,
          }),
        });
        const arrowStyle = new Style({
          image: new Icon({
            src: './assets/images/arrow-new.svg',
            anchor: [0.5, 0.5],
            rotateWithView: true,
            rotation: this.arrowRotation(definition.start, definition.end),
            scale: 50 / 1080,
          }),
        });

        const layer = new VectorLayer({
          source: new VectorSource({
            features: [routeFeature, arrowFeature],
          }),
          style: (feature) => (feature === routeFeature ? routeStyle : arrowStyle),
          className: this.getLayerClassName(MapPane.Path, MapPane.Path),
          zIndex: this.getPaneZIndex(MapPane.Path, MapPane.Path),
          updateWhileAnimating: this.#interactionSurfaceBoostEnabled,
          updateWhileInteracting: this.#interactionSurfaceBoostEnabled,
          renderBuffer: this.#interactionSurfaceBoostEnabled ? 512 : 100,
        });
        this.configureLayerPane(layer, MapPane.Path, MapPane.Path);
        this.applyInteractionSurfaceBoostToLayer(layer);

        this.#map.addLayer(layer);
        this.#paintedArrowLayer = layer;
        this.requestVectorSurfaceRepaint();
      });
    });
  }

  clearArrow(): void {
    if (this.#paintedArrowLayer && this.#map) {
      this.#map.removeLayer(this.#paintedArrowLayer);
      this.requestVectorSurfaceRepaint();
    }
    this.#paintedArrowLayer = undefined;
  }

  initializeCanvas(
    identifier: ElementIdentifier,
    nativeElement: HTMLElement,
    defaults: { zoom: number; minZoom: number; maxZoom: number }
  ): void {
    this.#destroyed = false;
    this.#interactionSurfaceBoostEnabled = true;
    this.#defaults = defaults;
    this.#projection = new Projection({
      code: 'cue-map-openlayers-projection',
      units: 'pixels',
      extent: [0, -1, 1, 0],
    });

    this.#zone.runOutsideAngular(() => {
      const view = new View({
        projection: this.#projection,
        center: [0, 0],
        zoom: defaults.zoom,
        minZoom: defaults.minZoom,
        maxZoom: defaults.maxZoom,
        constrainResolution: false,
        enableRotation: this.supportsRotation(),
      });

      this.#map = new OlMap({
        target: nativeElement,
        layers: [],
        view,
        controls: defaultControls({
          zoom: false,
          rotate: false,
          attribution: false,
        }),
      });
      this.configurePaneStacking();
      this.syncLeafletZoomScale();
      this.startFpsMonitor();
    });

    this.#boundResizeObserver = new ResizeObserver(() => {
      this.#map?.updateSize();
    });
    this.#boundResizeObserver.observe(nativeElement);
    this.customMapControls(nativeElement);

    this.#zone.runOutsideAngular(() => {
      if (!this.#map) {
        return;
      }
      this.#map.on('movestart', () => {
        this.#moving = true;
        this.requestVectorSurfaceRepaint();
      });
      this.#map.on('moveend', () => {
        this.#moving = false;
        this.#zooming = false;
        this.requestVectorSurfaceRepaint();
        this.refreshMarkerZIndices();
      });
      this.#map.on('singleclick', (event) => {
        this.#map?.forEachFeatureAtPixel(event.pixel, (featureLike) => {
          const feature = featureLike as Feature<Geometry>;
          const type = feature.get('cueElementType');
          const interactive = feature.get('cueInteractive') === true;
          const foundIdentifier = feature.get('cueElementIdentifier') as ElementIdentifier | undefined;
          if (!foundIdentifier) {
            return false;
          }
          if (type === 'layer' && interactive) {
            this.#layerClicked.next(foundIdentifier);
            return true;
          }
          return false;
        });
      });
      this.#map.on('pointermove', (event) => {
        if (event.dragging) {
          this.setMapCursor('');
          return;
        }

        let interactiveLayerHit = false;
        this.#map?.forEachFeatureAtPixel(event.pixel, (featureLike) => {
          const feature = featureLike as Feature<Geometry>;
          if (feature.get('cueElementType') === 'layer' && feature.get('cueInteractive') === true) {
            interactiveLayerHit = true;
            return true;
          }
          return false;
        });
        this.setMapCursor(interactiveLayerHit ? 'pointer' : '');
      });

      const targetElement = this.#map.getTargetElement();
      if (targetElement) {
        this.#targetPointerLeaveHandler = () => this.setMapCursor('');
        targetElement.addEventListener('pointerleave', this.#targetPointerLeaveHandler);
      }

      this.#map.getView().on('change:resolution', () => {
        this.publishCurrentZoom();
        this.#zooming = true;
        this.requestVectorSurfaceRepaint();
        this.refreshMarkerZIndices();
        this.updateZoomButtonState();
        if (this.#zoomingEndTimer) {
          clearTimeout(this.#zoomingEndTimer);
        }
        this.#zoomingEndTimer = setTimeout(() => {
          if (!this.#moving) {
            this.#zooming = false;
          }
        }, 60);
      });
    });

    this.publishCurrentZoom();
    this.#mapReady.next(true);
  }

  addImage(identifier: ElementIdentifier, src: string, width: number, height: number, fitMap: boolean): void {
    this.scheduleMapAvailable(() => {
      if (!this.#map || !this.#projection) {
        return;
      }
      const extent = this.imageExtent(width, height);
      this.#projection.setExtent(extent);

      const source = new ImageStatic({
        url: src,
        imageExtent: extent,
        projection: this.#projection,
      });
      const layer = new ImageLayer({
        source,
        className: this.getLayerClassName(MapPane.Map, MapPane.Map),
        zIndex: this.getPaneZIndex(MapPane.Map, MapPane.Map),
      });
      this.configureLayerPane(layer, MapPane.Map, MapPane.Map);
      this.#map.addLayer(layer);
      this.#imageOverlays.set(identifier, { layer, extent });
      this.#imageAppended.next(identifier);
      if (fitMap) {
        this.fitExtent(extent, [0, 0, 0, 0], false);
      }
    });
  }

  focusImage(identifier: ElementIdentifier) {
    const foundOverlay = this.#imageOverlays.get(identifier);
    if (!foundOverlay) {
      return;
    }

    if (this.#debug) {
      console.log('Focusing image', identifier, foundOverlay.extent);
    }

    this.executeWhenStationary(() => {
      this.fitExtent(foundOverlay.extent, [0, 0, 0, 0], false);
    });
  }

  setImageSrc(identifier: ElementIdentifier, src: string, width: number, height: number, fitMap: boolean): void {
    const foundOverlay = this.#imageOverlays.get(identifier);
    if (!foundOverlay || !this.#projection) {
      return;
    }
    const extent = this.imageExtent(width, height);
    this.#projection.setExtent(extent);
    foundOverlay.layer.setSource(
      new ImageStatic({
        url: src,
        imageExtent: extent,
        projection: this.#projection,
      })
    );
    foundOverlay.extent = extent;

    if (fitMap) {
      setTimeout(() => {
        this.executeWhenStationary(() => {
          if (this.#debug) {
            console.log('Fitting map after image source update');
          }
          this.fitExtent(extent, [0, 0, 0, 0], false);
        });
      }, 1);
    }
  }

  setResetZoom(identifier: ElementIdentifier, number: number): void {
    if (this.#defaults) {
      this.#defaults.zoom = number;
    }
  }

  setZoom(identifier: ElementIdentifier, number: number): void {
    const view = this.#map?.getView();
    if (!view) {
      return;
    }
    if (!Number.isFinite(number)) {
      return;
    }
    if (this.#debug) {
      console.log('Setting zoom to', number);
    }
    view.setZoom(number);
    this.publishCurrentZoom();
    this.updateZoomButtonState();
  }

  setMinZoom(identifier: ElementIdentifier, number: number): void {
    if (this.#debug) {
      console.log('Set min zoom', identifier, number);
    }
    if (this.#defaults) {
      this.#defaults.minZoom = number;
    }
    const view = this.#map?.getView();
    view?.setMinZoom(number);
    this.syncLeafletZoomScale();
    this.updateZoomButtonState();
  }

  setMaxZoom(identifier: ElementIdentifier, number: number): void {
    if (this.#debug) {
      console.log('Set max zoom', identifier, number);
    }
    if (this.#defaults) {
      this.#defaults.maxZoom = number;
    }
    const view = this.#map?.getView();
    view?.setMaxZoom(number);
    this.updateZoomButtonState();
  }

  private fitGeometry(geometry?: Geometry | null, padding: Padding = [0, 0, 0, 0], animate = false) {
    if (!geometry) {
      return;
    }
    this.fitExtent(geometry.getExtent() as MapExtent, padding, animate);
  }

  private fitExtent(extent: MapExtent, padding: Padding = [0, 0, 0, 0], animate = false) {
    if (!this.#map) {
      return;
    }
    const size = this.#map.getSize();
    if (!size) {
      return;
    }
    this.#map.getView().fit(extent, {
      size,
      padding,
      duration: animate ? 250 : 0,
      nearest: true,
    });
    this.publishCurrentZoom();
  }

  private normalizeRotation(rotation: number) {
    const fullTurn = Math.PI * 2;
    let normalized = rotation % fullTurn;
    if (normalized <= -Math.PI) {
      normalized += fullTurn;
    }
    if (normalized > Math.PI) {
      normalized -= fullTurn;
    }
    return normalized;
  }

  private publishCurrentZoom() {
    const zoom = this.#map?.getView().getZoom();
    if (typeof zoom !== 'number' || !Number.isFinite(zoom)) {
      return;
    }

    if (this.#lastPublishedZoom === zoom) {
      return;
    }
    this.#lastPublishedZoom = zoom;
    this.#zoomChanged.next(zoom);
  }

  private syncLeafletZoomScale() {
    if (!this.#map) {
      return;
    }
    const view = this.#map.getView() as any;
    // Leaflet CRS.Simple resolution model: resolution = 1 / 2^zoom.
    // `minZoom` is only a constraint in Leaflet, it does not shift the whole zoom scale.
    // When maxResolution is derived from minZoom, OpenLayers reports zoom values offset by that amount.
    const maxResolution = 1;
    if (typeof view.getUpdatedOptions_ === 'function' && typeof view.applyOptions_ === 'function') {
      view.applyOptions_(view.getUpdatedOptions_({ maxResolution }));
      view.resolveConstraints(0);
    }
    this.updateZoomButtonState();
  }

  private executeWhenStationary(action: () => void) {
    if (!this.#map) {
      return;
    }
    if (this.#moving) {
      this.#map.once('moveend', () => action());
      return;
    }
    if (this.#zooming) {
      setTimeout(() => action(), 0);
      return;
    }
    action();
  }

  private imageExtent(width: number, height: number): MapExtent {
    // Leaflet implementation uses CRS.Simple + unproject(..., 2), which divides by 4 and inverts Y.
    return [0, -height / 4, width / 4, 0];
  }

  private extractPointCoordinate(geoJson: any): [number, number] | null {
    if (!geoJson) {
      return null;
    }

    const geometry = geoJson.type === 'Feature' ? geoJson.geometry : geoJson;
    const directPoint = this.parsePointFromGeometryLike(geometry ?? geoJson);
    if (directPoint) {
      return directPoint;
    }

    // Fallback: parse via OpenLayers GeoJSON reader and use geometry center if needed.
    try {
      const featureGeoJson =
        geoJson.type === 'Feature'
          ? geoJson
          : {
              type: 'Feature',
              geometry: geometry ?? geoJson,
              properties: {},
            };
      const feature = this.#geoJson.readFeature(featureGeoJson) as Feature<Geometry>;
      const foundGeometry = feature.getGeometry();
      if (!foundGeometry) {
        return null;
      }
      if (foundGeometry instanceof Point) {
        const coordinates = foundGeometry.getCoordinates();
        const parsed = this.parsePointFromGeometryLike({
          type: 'Point',
          coordinates,
        });
        if (parsed) {
          return parsed;
        }
      }
      const extent = foundGeometry.getExtent();
      const centerX = (extent[0] + extent[2]) / 2;
      const centerY = (extent[1] + extent[3]) / 2;
      if (Number.isFinite(centerX) && Number.isFinite(centerY)) {
        return [centerX, centerY];
      }
    } catch (error) {
      if (this.#debug) {
        console.warn('Unable to parse marker geometry', geoJson, error);
      }
    }
    return null;
  }

  private parseNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value.replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private parsePointPair(x: unknown, y: unknown): [number, number] | null {
    const parsedX = this.parseNumber(x);
    const parsedY = this.parseNumber(y);
    if (parsedX === null || parsedY === null) {
      return null;
    }
    return [parsedX, parsedY];
  }

  private parsePointFromGeometryLike(geometry: any): [number, number] | null {
    if (!geometry || typeof geometry !== 'object') {
      return null;
    }

    const geometryType = geometry.type;
    const coordinates = geometry.coordinates;
    if (geometryType === 'Point') {
      if (Array.isArray(coordinates)) {
        return this.parsePointPair(coordinates[0], coordinates[1]);
      }
      if (coordinates && typeof coordinates === 'object') {
        return (
          this.parsePointPair((coordinates as any).x, (coordinates as any).y) ??
          this.parsePointPair((coordinates as any).lng, (coordinates as any).lat)
        );
      }
    }

    return (
      this.parsePointPair((geometry as any).x, (geometry as any).y) ??
      this.parsePointPair((geometry as any).lng, (geometry as any).lat)
    );
  }

  private createLayerFeature(geoJson: any, radius?: number): Feature<Geometry> | null {
    if (!geoJson) {
      return null;
    }
    const geometry = geoJson.type === 'Feature' ? geoJson.geometry : geoJson;
    if (!geometry) {
      return null;
    }

    if (radius && geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
      const parsed = this.parsePointPair(geometry.coordinates[0], geometry.coordinates[1]);
      if (!parsed) {
        return null;
      }
      return new Feature(new CircleGeom(parsed, radius));
    }

    const featureGeoJson =
      geoJson.type === 'Feature'
        ? geoJson
        : {
            type: 'Feature',
            geometry: geoJson,
            properties: {},
          };

    const feature = this.#geoJson.readFeature(featureGeoJson) as Feature<Geometry>;
    const featureGeometry = feature.getGeometry();
    if (!featureGeometry) {
      return null;
    }
    return feature;
  }

  private createLayerStyle(layerDefinition: LayerDefinition): Style {
    return new Style({
      stroke: new Stroke({
        color: layerDefinition.color,
        width: layerDefinition.weight,
      }),
      fill: new Fill({
        color: layerDefinition.fill
          ? this.colorWithOpacity(layerDefinition.fillColor, layerDefinition.fillOpacity)
          : [0, 0, 0, 0],
      }),
    });
  }

  private colorWithOpacity(color: string, opacity?: number): Color {
    let parsed: Color;
    try {
      parsed = [...asArray(color)] as Color;
    } catch {
      parsed = [0, 0, 0, 1];
    }
    parsed[3] = this.clampOpacity(opacity ?? parsed[3] ?? 1);
    return parsed;
  }

  private clampOpacity(opacity: number): number {
    return Math.max(0, Math.min(1, opacity));
  }

  private toLineCoordinates(pointlist: PolylineDefinition['pointlist']): [number, number][] {
    return (pointlist ?? []).map((point) => [point.lng, point.lat]);
  }

  private arrowRotation(start: PaintArrowDefinition['start'], end: PaintArrowDefinition['end']): number {
    const deltaX = end.lng - start.lng;
    const deltaY = end.lat - start.lat;

    if (deltaX === 0 && deltaY === 0) {
      return 0;
    }

    return Math.atan2(deltaX, deltaY);
  }

  private plusSvg() {
    return `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 3a1 1 0 1 1 2 0v4h4a1 1 0 1 1 0 2H9v4a1 1 0 1 1-2 0V9H3a1 1 0 1 1 0-2h4V3Z" />
      </svg>
    `;
  }

  private minusSvg() {
    return `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 7a1 1 0 0 0 0 2h10a1 1 0 1 0 0-2H3Z" />
      </svg>
    `;
  }

  private resetSvg() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.663 8.003h5.33a.666.666 0 0 0 0-1.333h-3.721l5.525-5.525a.666.666 0 1 0-.942-.942L17.33 5.728V2.007a.666.666 0 1 0-1.333 0v5.33c0 .367.299.666.666.666ZM7.337 15.997h-5.33a.666.666 0 1 0 0 1.333h3.721L.203 22.855a.666.666 0 1 0 .942.942l5.525-5.525v3.721a.666.666 0 0 0 1.333 0v-5.33a.666.666 0 0 0-.666-.666ZM18.272 17.33h3.721a.666.666 0 0 0 0-1.333h-5.33a.666.666 0 0 0-.666.666v5.33a.666.666 0 1 0 1.333 0v-3.721l5.525 5.525a.666.666 0 0 0 .942-.942l-5.525-5.525ZM7.337 1.34a.666.666 0 0 0-.667.667v3.721L1.145.203a.666.666 0 0 0-.942.942L5.728 6.67H2.007a.666.666 0 1 0 0 1.333h5.33a.666.666 0 0 0 .666-.666v-5.33a.666.666 0 0 0-.666-.666Z"/>
      </svg>
    `;
  }

  private locationSvg() {
    return `
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.3931 10.9998C15.3931 13.4298 13.4232 15.3998 10.9932 15.3998C8.56322 15.3998 6.59346 13.4298 6.59346 10.9998C6.59346 8.56987 8.56322 6.60011 10.9932 6.60011C13.4232 6.60011 15.3931 8.56987 15.3931 10.9998Z" fill="currentColor"/>
        <path d="M21.3401 10.3398H19.869C19.5383 5.94654 16.0467 2.45435 11.6534 2.12323V0.660011C11.6534 0.295464 11.3577 0 10.9934 0C10.6288 0 10.3334 0.295464 10.3334 0.660011V2.12323C5.93989 2.45435 2.4481 5.94654 2.11739 10.3398H0.660011C0.295464 10.3398 0 10.6355 0 10.9998C0 11.3644 0.295464 11.6599 0.660011 11.6599H2.11739C2.4481 16.0531 5.93989 19.5453 10.3332 19.8765V21.3401C10.3332 21.7046 10.6286 22.0001 10.9932 22.0001C11.3575 22.0001 11.653 21.7046 11.653 21.3401V19.8765C16.0465 19.5455 19.5383 16.0533 19.869 11.6599H21.3401C21.7046 11.6599 22.0001 11.3644 22.0001 10.9998C22.0001 10.6355 21.7046 10.3398 21.3401 10.3398ZM10.9932 18.5895C6.80171 18.5893 3.40398 15.1911 3.40398 10.9996C3.40418 6.80796 6.80232 3.41022 10.9938 3.41022C15.1855 3.41022 18.5834 6.80836 18.5834 10.9998C18.5784 15.1897 15.1831 18.5848 10.9932 18.5895Z" fill="currentColor"/>
      </svg>
    `;
  }

  private rotateLeftSvg() {
    return `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.5 7.6V4.5L3.8 9.2l4.7 4.7v-3.1h3.9a5.6 5.6 0 1 1-5.6 5.6 1.2 1.2 0 1 0-2.4 0 8 8 0 1 0 8-8H8.5Z"/>
      </svg>
    `;
  }

  private rotateRightSvg() {
    return `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.5 7.6h-3.9a8 8 0 1 0 8 8 1.2 1.2 0 1 0-2.4 0 5.6 5.6 0 1 1-5.6-5.6h3.9v3.1l4.7-4.7-4.7-4.7v3.1Z"/>
      </svg>
    `;
  }

  private gpsAutoRotateSvg() {
    return `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2.4c-2.6 0-4.7 2.1-4.7 4.7 0 1.8 1 3.3 2.4 4.1V16c0 .7.6 1.3 1.3 1.3s1.3-.6 1.3-1.3v-4.8c1.5-.8 2.4-2.3 2.4-4.1 0-2.6-2.1-4.7-4.7-4.7Zm0 2.4c1.3 0 2.3 1 2.3 2.3s-1 2.3-2.3 2.3-2.3-1-2.3-2.3 1-2.3 2.3-2.3Z"/>
        <path d="M5 14.4a1.2 1.2 0 0 0 0 2.4h2.4a1.2 1.2 0 0 0 0-2.4H5Zm11.6 0a1.2 1.2 0 0 0 0 2.4H19a1.2 1.2 0 1 0 0-2.4h-2.4ZM8.3 19a1.2 1.2 0 1 0-1.7 1.7l1.7 1.7a1.2 1.2 0 1 0 1.7-1.7L8.3 19Zm7.4 0-1.7 1.7a1.2 1.2 0 1 0 1.7 1.7l1.7-1.7a1.2 1.2 0 1 0-1.7-1.7Z"/>
      </svg>
    `;
  }
}
